// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
// import * as openapi from 'registry-app-read-api-ts-client';
import * as jsonApiV2Openapi from '@digitalasset/json-api-ts-client';
import { ContractId, InterfaceCompanion } from '@daml/types';

/*
const requiredInterfaceViews: Partial<
  Record<string, InterfaceCompanion<object, unknown, string>[]>
> = {};
*/

export default class InterfaceContract<I> {
  readonly interfaceId: string;

  readonly templateId: string;

  readonly contractId: ContractId<I>;

  readonly views: Partial<Record<string, unknown>>;

  readonly createdEventBlob?: string;

  constructor(
    interfaceId: string,
    templateId: string,
    contractId: ContractId<I>,
    views: Record<string, unknown>,
    createdEventBlob?: string,
  ) {
    this.interfaceId = interfaceId;
    this.templateId = templateId;
    this.contractId = contractId;
    this.views = views;
    this.createdEventBlob = createdEventBlob;
  }

  view<TV extends object>(companion: InterfaceCompanion<TV, unknown, string>): TV {
    const v = this.views[companion.templateId];
    if (!v) {
      throw new Error(`No view for interface ${companion.templateId}`);
    }
    return v as TV;
  }

  /* Re-enable once we have interface support. Currently this fails to build because InterfaceContract is not referenced anywhere
  static fromOpenAPI<T extends object, K, I extends string>(
    companion: InterfaceCompanion<T, K, I>,
    c: openapi.InterfaceContract,
  ): InterfaceContract<T> {
    const requiredViews = requiredInterfaceViews[companion.templateId];
    if (!requiredViews) {
      throw new Error(`${companion.templateId} is missing from requiredInterfaceViews`);
    }
    const expectedViewIds: string[] = requiredViews.map((i) => i.templateId).sort();
    const actualViewIds: string[] = Object.keys(c.interface_views).sort();
    if (
      expectedViewIds.length !== actualViewIds.length ||
      !expectedViewIds.every((v, i) => v === actualViewIds[i])
    ) {
      throw new Error(
        `Expected view ids ${expectedViewIds.toString()} do not match actual view ids ${actualViewIds.toString()}`,
      );
    }

    const decodedViews = Object.fromEntries(
      requiredViews.map((v) => [
        v.templateId,
        v.decoder.runWithException(c.interface_views[v.templateId]),
      ]),
    );

    return new InterfaceContract(
      c.interface_id,
      c.template_id,
      c.contract_id as ContractId<T>,
      decodedViews,
      c.createdEventBlob,
    );
  }
  */

  static fromJsonAPIV2<T extends object, K, I extends string>(
    interfaceCompanion: InterfaceCompanion<T, K, I>,
    c: jsonApiV2Openapi.CreatedEvent,
  ): InterfaceContract<T> {
    const view = c.interfaceViews?.find(
      (i) => i.interfaceId === interfaceCompanion.templateIdWithPackageId,
    );
    if (!view) {
      throw new Error(`No view found for interface ${interfaceCompanion.templateId}`);
    }
    const decodedView = interfaceCompanion.decoder.runWithException(view.viewValue);
    return new InterfaceContract(
      interfaceCompanion.templateId,
      c.templateId,
      c.contractId as ContractId<T>,
      // TODO(#3991): look into how we can support all interface views of the template implemented. it is only returning the queried interface for now.
      {
        [interfaceCompanion.templateId]: decodedView,
      },
      c.createdEventBlob,
    );
  }
}
